import { requestApi } from "../api/client.js";

export type RecordOutcome = "fail" | "na" | "pass" | "warn";

export interface RecordEvaluation {
  inputs: Record<string, unknown>;
  outcome: "fail" | "not_applicable" | "pass" | "warn";
  params: Record<string, unknown>;
  rule_code: string;
  rule_version: number;
}

export interface RecordEntry {
  attachments: { attachment_id: string; caption: string | null }[];
  current_revision: number;
  detail?: Record<string, unknown>;
  dst_resolution: "earlier" | "later" | "unambiguous";
  evaluations: RecordEvaluation[];
  follow_up_id?: string | null;
  form_version: number | null;
  id: string;
  location_id: string;
  note: string | null;
  occurred_at: string;
  occurred_tz: string;
  overall_result: RecordOutcome | null;
  payload?: Record<string, unknown>;
  performed_by_staff_id: string;
  plan_instance_id: string | null;
  recorded_at: string;
  record_type_code: string;
  source: string;
  status: "active" | "voided";
  submitted_by_user_id: string | null;
  task_occurrence_id: string | null;
  voided_reason: string | null;
}

export interface RecordRevision {
  changed_at: string;
  changed_by: string;
  changed_fields: string[];
  id: string;
  new_values: Record<string, unknown>;
  previous_values: Record<string, unknown>;
  reason: string | null;
  revision_no: number;
}

export interface RecordFilters {
  includeVoided: boolean;
  occurredFrom?: string;
  occurredTo?: string;
  outcome?: RecordOutcome;
  performer?: string;
  recordType?: string;
}

interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export function listRecords(
  locationId: string,
  filters: RecordFilters,
  cursor?: string,
): Promise<Page<RecordEntry>> {
  const query = new URLSearchParams({
    include_voided: String(filters.includeVoided),
    limit: "50",
    location_id: locationId,
  });
  if (filters.recordType) query.set("record_type_code", filters.recordType);
  if (filters.outcome) query.set("overall_result", filters.outcome);
  if (filters.performer) query.set("performed_by", filters.performer);
  if (filters.occurredFrom) query.set("occurred_from", filters.occurredFrom);
  if (filters.occurredTo) query.set("occurred_to", filters.occurredTo);
  if (cursor) query.set("cursor", cursor);
  return requestApi<Page<RecordEntry>>(`/records?${query.toString()}`);
}

export function getRecord(id: string): Promise<RecordEntry> {
  return requestApi<RecordEntry>(`/records/${id}`);
}

export function editRecord(
  id: string,
  input: {
    based_on_revision: number;
    detail?: Record<string, unknown>;
    note?: string | null;
    occurred_at?: string;
    performed_by_staff_id?: string;
    payload?: Record<string, unknown>;
    reason?: string | null;
  },
): Promise<RecordEntry> {
  return requestApi<RecordEntry>(`/records/${id}`, {
    body: JSON.stringify(input),
    method: "PATCH",
  });
}

export function voidRecord(id: string, reason: string): Promise<RecordEntry> {
  return requestApi<RecordEntry>(`/records/${id}/void`, {
    body: JSON.stringify({ reason }),
    method: "POST",
  });
}

export function getRecordRevisions(id: string): Promise<RecordRevision[]> {
  return requestApi<RecordRevision[]>(`/records/${id}/revisions`);
}

export function getAttachmentDownload(
  id: string,
  variant: "display" | "original" | "thumb" = "display",
): Promise<{ download_url: string; expires_at: string }> {
  return requestApi(`/attachments/${id}/download?variant=${variant}`);
}

export interface RecordTypeChoice {
  category: string;
  code: string;
  detail_table: string | null;
  requires_asset: boolean;
  reserved: boolean;
  sort_order: number;
  storage: "payload" | "typed";
  supports_photos: "none" | "optional" | "requested";
  title: string;
}

export interface FormDefinition {
  id: string;
  json_schema: JsonSchema;
  record_type_code: string;
  status: "active" | "draft" | "retired";
  ui_schema: Record<string, unknown>;
  version: number;
}

export interface JsonSchema {
  const?: unknown;
  else?: JsonSchema;
  enum?: string[];
  format?: string;
  items?: JsonSchema;
  maxLength?: number;
  maximum?: number;
  minItems?: number;
  minimum?: number;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  if?: JsonSchema;
  then?: JsonSchema;
  type?: "array" | "boolean" | "integer" | "number" | "object" | "string";
}

export interface RecordContentBundle {
  form_definitions: FormDefinition[];
  record_types: RecordTypeChoice[];
}

export async function getRecordContent(): Promise<RecordContentBundle> {
  const response = await requestApi<{
    bundle: RecordContentBundle | null;
    not_modified: boolean;
    version: number;
  }>("/content/bundle");
  return response.bundle ?? { form_definitions: [], record_types: [] };
}

export async function getRecordTypes(): Promise<RecordTypeChoice[]> {
  const response = await getRecordContent();
  return response.record_types.filter((type) => !type.reserved);
}

export function uuidV7(
  now = Date.now(),
  random = crypto.getRandomValues(new Uint8Array(10)),
): string {
  const bytes = new Uint8Array(16);
  let timestamp = BigInt(now);
  for (let index = 5; index >= 0; index -= 1) {
    bytes[index] = Number(timestamp & 0xffn);
    timestamp >>= 8n;
  }
  bytes.set(random, 6);
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createRecord(input: {
  attachment_ids?: string[];
  detail?: Record<string, unknown>;
  dst_resolution: "earlier" | "later" | "unambiguous";
  id: string;
  location_id: string;
  note?: string | null;
  occurred_at: string;
  occurred_tz: string;
  payload?: Record<string, unknown>;
  performed_by_staff_id: string;
  record_type_code: string;
  source: "manual" | "task";
  task_occurrence_id?: string | null;
}): Promise<RecordEntry> {
  return requestApi("/records", {
    body: JSON.stringify(input),
    method: "POST",
  });
}

export function resultTone(
  outcome: RecordEntry["overall_result"],
): "attention" | "danger" | "neutral" | "positive" {
  return outcome === "pass"
    ? "positive"
    : outcome === "fail"
      ? "danger"
      : outcome === "warn"
        ? "attention"
        : "neutral";
}

export function displayRecordValue(value: unknown): string {
  if (value === null || value === undefined || value === "")
    return "Not recorded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number")
    return new Intl.NumberFormat("en-NZ", { maximumFractionDigits: 3 }).format(
      value,
    );
  if (Array.isArray(value))
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value).replaceAll("_", " ");
}

export function recordSummary(record: RecordEntry): string {
  const values = record.detail ?? record.payload ?? {};
  for (const key of [
    "value_c",
    "internal_temp_c",
    "ph_finish",
    "meter_reading_aw",
    "result",
    "what_went_wrong",
    "affected_food",
    "food_description",
    "task_description",
  ]) {
    if (values[key] !== undefined && values[key] !== null) {
      const suffix = key.endsWith("_c") ? "°C" : "";
      return `${displayRecordValue(values[key])}${suffix}`;
    }
  }
  if (Array.isArray(values.items))
    return `${values.items.length} delivery item${values.items.length === 1 ? "" : "s"}`;
  return record.note ?? "Evidence recorded";
}

export function fieldLabel(key: string): string {
  return key
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bId\b/g, "ID");
}
